import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Search, Loader2, ImagePlus } from "lucide-react";
import { toast } from "sonner";

/**
 * MediaPicker — modal that lists media assets from the Media Library
 * and returns a URL via onPick(url). Also supports direct upload.
 */
export default function MediaPicker({ open, onClose, onPick, folder = "" }) {
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    if (!open) return;
    setBusy(true);
    const params = new URLSearchParams();
    if (folder) params.set("folder", folder);
    if (q) params.set("q", q);
    params.set("limit", "80");
    api.get(`/admin/media?${params.toString()}`)
      .then((r) => setAssets(r.data))
      .finally(() => setBusy(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [open, q, folder]);

  const uploadNew = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder || "uploads");
    try {
      const { data } = await api.post("/admin/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onPick?.(data.url);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="media-picker">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl max-h-[85vh] rounded-3xl bg-white p-6 shadow-2xl border border-[#E5E7EB] flex flex-col">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-2xl tracking-tight">Pick from Media Library</h2>
          <button onClick={onClose} className="h-9 w-9 rounded-full border border-[#E5E7EB] grid place-items-center" data-testid="media-picker-close"><X className="w-4 h-4"/></button>
        </div>
        <div className="mt-4 flex gap-3 items-center flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search library…"
              className="h-10 pl-9 pr-3 rounded-full border border-[#E5E7EB] bg-white text-sm outline-none focus:border-blue-400 w-full"
              data-testid="media-picker-search"/>
          </div>
          <label className="btn-primary h-10 px-4 text-sm inline-flex items-center gap-2 cursor-pointer" data-testid="media-picker-upload">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <ImagePlus className="w-4 h-4"/>} Upload new
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadNew(e.target.files?.[0])}/>
          </label>
        </div>
        <div className="mt-4 flex-1 overflow-auto">
          {busy ? (
            <div className="grid place-items-center py-12 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin"/></div>
          ) : assets.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground text-sm">No media yet — upload one above.</div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {assets.map((a) => (
                <button key={a.id} type="button" data-testid={`media-picker-item-${a.id}`}
                  onClick={() => { onPick?.(a.url); onClose?.(); }}
                  className="group aspect-square rounded-xl overflow-hidden border border-[#E5E7EB] bg-[#F3F6FA] hover:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]">
                  <img src={a.url} alt={a.alt || a.original_filename} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform"/>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
